'use client';

import { Booking, BookingStatus, BookingPaymentStatus, BOOKING_STATUSES, BOOKING_PAYMENT_STATUSES } from '@/lib/bookings/types';

interface BookingSummaryProps {
  booking: {
    id?: string;
    bookingNumber: string;
    customer?: { id?: string; firstName: string; lastName: string; email?: string } | null;
    status: BookingStatus;
    paymentStatus: BookingPaymentStatus;
    startDate?: string;
    endDate?: string;
    totalAmount: number;
    items?: { length: number } | any[];
    travellers?: any[];
  };
  moduleType?: 'stay' | 'trip';
  /** Show a compact version (smaller padding, fewer details) */
  compact?: boolean;
  /** Clickable — renders as a button/link */
  onClick?: () => void;
}

function formatDate(dateStr: string | undefined): string {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

function getStatusColor(bookingStatus: BookingStatus): string {
  const statusConfig = BOOKING_STATUSES.find((s) => s.value === bookingStatus);
  switch (statusConfig?.color) {
    case 'green':
      return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
    case 'yellow':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
    case 'red':
      return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
    case 'blue':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
  }
}

function getPaymentStatusColor(payStatus: BookingPaymentStatus): string {
  const statusConfig = BOOKING_PAYMENT_STATUSES.find((s) => s.value === payStatus);
  switch (statusConfig?.color) {
    case 'green':
      return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
    case 'yellow':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
    case 'red':
      return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
  }
}

function getStatusLabel(bookingStatus: BookingStatus): string {
  return BOOKING_STATUSES.find((s) => s.value === bookingStatus)?.label ?? bookingStatus;
}

function getPaymentStatusLabel(payStatus: BookingPaymentStatus): string {
  return BOOKING_PAYMENT_STATUSES.find((s) => s.value === payStatus)?.label ?? payStatus;
}

export function BookingSummary({ booking, moduleType, compact = false, onClick }: BookingSummaryProps) {
  const customerName = booking.customer
    ? `${booking.customer.firstName} ${booking.customer.lastName}`
    : '—';

  const itemCount = booking.items?.length ?? 0;
  const isClickable = !!onClick;

  const cardClass = isClickable
    ? 'cursor-pointer hover:shadow-md transition-shadow rounded-lg border border-stroke bg-white dark:border-dark-3 dark:bg-dark-2'
    : 'rounded-lg border border-stroke bg-white dark:border-dark-3 dark:bg-dark-2';

  const content = (
    <div className={`${cardClass} ${compact ? 'p-3' : 'p-4'}`}>
      {/* Header: Booking # + Module + Badges */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="font-semibold text-dark dark:text-white">
          #{booking.bookingNumber}
        </span>
        {moduleType && (
          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
            moduleType === 'stay'
              ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400'
              : 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400'
          }`}>
            {moduleType === 'stay' ? '🏨 Stay' : '✈️ Trip'}
          </span>
        )}
        <span className={`ml-auto rounded-full px-2 py-0.5 text-xs font-medium ${getStatusColor(booking.status)}`}>
          {getStatusLabel(booking.status)}
        </span>
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${getPaymentStatusColor(booking.paymentStatus)}`}>
          {getPaymentStatusLabel(booking.paymentStatus)}
        </span>
      </div>

      {/* Customer + Dates */}
      <div className={`space-y-1 ${compact ? 'mb-2' : 'mb-3'}`}>
        <p className="text-sm font-medium text-dark dark:text-white truncate">
          {customerName}
        </p>
        <p className={`text-xs text-body dark:text-gray-400 ${compact ? '' : 'text-body-secondary'}`}>
          {formatDate(booking.startDate)} – {formatDate(booking.endDate)}
        </p>
      </div>

      {/* Footer: Total + Item count */}
      {!compact && (
        <div className="flex items-center justify-between border-t border-stroke pt-3 dark:border-dark-3">
          <span className="text-xs text-body dark:text-gray-400">
            {itemCount} {itemCount === 1 ? 'item' : 'items'} booked
          </span>
          <span className="font-semibold text-dark dark:text-white">
            {formatCurrency(booking.totalAmount)}
          </span>
        </div>
      )}

      {compact && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-body dark:text-gray-400">
            {itemCount} {itemCount === 1 ? 'item' : 'items'}
          </span>
          <span className="text-sm font-semibold text-dark dark:text-white">
            {formatCurrency(booking.totalAmount)}
          </span>
        </div>
      )}
    </div>
  );

  if (isClickable) {
    return (
      <button type="button" onClick={onClick} className="w-full text-left">
        {content}
      </button>
    );
  }

  return content;
}
